import React from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useMoiCommerciaux } from '../Commerciaux/useMoiCommerciaux';
import { PrestationDossierModule } from './PrestationDossierModule';
import { EstimationDevisPage } from './EstimationDevisPage';
import { DevisPrestationModule } from './DevisPrestationModule';
import { SubscriptionsModule } from '../Abonnements/SubscriptionsModule';

export type VueDossiers = 'dossiers' | 'estimation' | 'devis' | 'contrats';

// Les anciennes adresses restent valables : chacune ouvre « Dossiers » sur son onglet.
const ONGLETS: { vue: VueDossiers; label: string; to: string }[] = [
    { vue: 'dossiers', label: 'Dossiers', to: '/suivi-prestation' },
    { vue: 'estimation', label: 'Estimation rapide', to: '/estimation-devis' },
    { vue: 'devis', label: 'Devis', to: '/devis-prestation' },
    { vue: 'contrats', label: 'Contrats annuels', to: '/abonnements' },
];

/**
 * Un seul module pour la chaîne demande → estimation → devis → contrat. Les écrans existants sont repris tels quels
 * sous des onglets : aucun réglage, aucune donnée ne bouge, seul le menu est simplifié (refonte Dossiers clients, étape 5).
 */
export function DossiersHub({ vue }: { vue: VueDossiers }) {
    const { user } = useAuth();
    const commercial = useMoiCommerciaux(user?.type === 'collaborator').moi?.role === 'commercial';
    const [modules, setModules] = React.useState<string[] | null>(null);

    React.useEffect(() => {
        let vivant = true;
        authApi.getMyModules()
            .then((liste: any) => {
                if (!vivant) return;
                setModules((Array.isArray(liste) ? liste : []).filter((m: any) => m && Number(m.is_active) === 1).map((m: any) => String(m.module_name)));
            })
            .catch(() => { if (vivant) setModules([]); });
        return () => { vivant = false; };
    }, []);

    // Liste inconnue ou vide (compte ancien jamais configuré) : on montre les onglets habituels, le serveur garde la main.
    const actif = (nom: string) => !modules || modules.length === 0 || modules.includes(nom);
    const visibles = ONGLETS.filter((o) => {
        if (o.vue === 'contrats') return !commercial && actif('contrats_abonnement');
        if (!actif('suivi_prestation')) return false;
        if (o.vue === 'devis') return !commercial;
        return true;
    });

    return (
        <div>
            {visibles.length > 1 && (
                <nav aria-label="Dossiers clients" className="px-6 pt-5 max-w-6xl mx-auto">
                    <div className="flex flex-wrap gap-1 border-b border-border">
                        {visibles.map((o) => {
                            const courant = o.vue === vue;
                            return (
                                <Link key={o.vue} to={o.to} aria-current={courant ? 'page' : undefined}
                                    className={'px-4 py-2 text-sm font-bold -mb-px border-b-2 transition-colors ' + (courant ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-content-muted hover:text-content')}>
                                    {o.label}
                                </Link>
                            );
                        })}
                    </div>
                </nav>
            )}
            {vue === 'dossiers' && <PrestationDossierModule />}
            {vue === 'estimation' && <EstimationDevisPage />}
            {vue === 'devis' && <DevisPrestationModule />}
            {vue === 'contrats' && <div className="p-6 max-w-6xl mx-auto"><SubscriptionsModule /></div>}
        </div>
    );
}
